// src/PollsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc, where } from 'firebase/firestore';
import { auth, db } from './firebase.js';
import moment from 'moment';
import { Pie } from 'react-chartjs-2'; // Correct import for Pie from react-chartjs-2
import { CircularProgress } from '@mui/material'; // Import CircularProgress from @mui/material
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement, // Added for pie charts
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components globally to avoid init issues
ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement, // Registered for pie charts
  Title,
  Tooltip,
  Legend
);

// Separate component for poll results to encapsulate chart logic
const PollResults = ({ poll, voteCounts, isMobile }) => {
  if (!poll || !poll.options) return <p>No results available</p>;
  if (!voteCounts) return <p>No votes yet.</p>; // Added check for undefined voteCounts

  const totalVotes = Object.values(voteCounts).reduce((sum, count) => sum + count, 0);
  if (totalVotes === 0) return <p>No votes yet.</p>;

  const data = {
    labels: poll.options.map(opt => opt.text),
    datasets: [{
      data: poll.options.map((opt, idx) => voteCounts[idx] || 0),
      backgroundColor: [
        'rgba(26, 60, 52, 0.6)', // Dark teal for first option
        'rgba(34, 139, 34, 0.6)', // Forest green for second option
        'rgba(46, 139, 87, 0.6)', // Sea green for third option
        'rgba(60, 179, 113, 0.6)' // Medium sea green for fourth option
      ],
      borderColor: [
        '#1a3c34',
        '#228b22',
        '#2e8b57',
        '#3cb371'
      ],
      borderWidth: 1
    }]
  };
  const chartStyle = {
    width: isMobile ? '100%' : '450px',
    height: isMobile ? '300px' : '450px',
    maxWidth: '450px',
    margin: '0 auto'
  };

  return (
    <div style={chartStyle}>
      <Pie data={data} />
    </div>
  );
};

const PollsPage = ({ user, isMobile }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLotIds, setUserLotIds] = useState([]); // Array of lot IDs owned by user
  const [selectedOptions, setSelectedOptions] = useState({});  // Poll ID -> selected option
  const [selectedLots, setSelectedLots] = useState({});  // Poll ID -> selected lot for voting
  const [timers, setTimers] = useState({});  // Poll ID -> remaining time string
  const [votedPolls, setVotedPolls] = useState({});  // Poll ID -> array of voted lot IDs
  const [results, setResults] = useState({});  // Poll ID -> {option: count}
  const [votingLoading, setVotingLoading] = useState({});  // Poll ID -> true if voting in progress
  const [dataLoaded, setDataLoaded] = useState(false); // Flag to prevent re-loading data

  const getUserVotedLots = useCallback(async (pollId) => {
    if (!user || userLotIds.length === 0) return [];
    console.log('Fetching voted lots for poll:', pollId, 'user:', user.uid);
    const votedLots = [];
    for (const lotId of userLotIds) {
      const voteRef = doc(db, 'polls', pollId, 'votes', lotId);
      const voteSnap = await getDoc(voteRef);
      if (voteSnap.exists()) {
        votedLots.push(lotId);
      }
    }
    console.log('Voted lots:', votedLots);
    return votedLots;
  }, [user, userLotIds]);

  const getResults = useCallback(async (pollId) => {
    console.log('Fetching results for poll:', pollId);
    const votesSnap = await getDocs(collection(db, 'polls', pollId, 'votes'));
    const voteCounts = {};
    votesSnap.docs.forEach(doc => {
      const optionIndex = doc.data().optionIndex;
      voteCounts[optionIndex] = (voteCounts[optionIndex] || 0) + 1;
    });
    console.log('Results:', voteCounts);
    return voteCounts;
  }, []);

  useEffect(() => {
    console.log('PollsPage mounted. User:', user ? 'present' : 'absent', 'isMobile:', isMobile);
    
    const fetchUserLots = async () => {
      if (!user) {
        console.log('No user, skipping fetchUserLots');
        return;
      }
      try {
        console.log('Fetching user lots for UID:', user.uid);
        const ownersQuery = query(collection(db, 'owners'), where('OwnerId', '==', user.uid));
        const ownersQuery2 = query(collection(db, 'owners'), where('OwnerId2', '==', user.uid));
        const [snapshot1, snapshot2] = await Promise.all([getDocs(ownersQuery), getDocs(ownersQuery2)]);
        const lots = [...snapshot1.docs, ...snapshot2.docs].map(doc => doc.id); // e.g., 'tract1'
        const uniqueLots = [...new Set(lots)]; // Deduplicate if in both
        console.log('User lots:', uniqueLots);
        setUserLotIds(uniqueLots);
      } catch (err) {
        console.error('Error fetching user lots:', err);
      }
    };

    fetchUserLots();
  }, [user]);

  useEffect(() => {
    const fetchPolls = async () => {
      console.log('Fetching polls...');
      try {
        const q = query(collection(db, 'polls'), orderBy('startTime', 'desc'));
        const snapshot = await getDocs(q);
        console.log('Polls snapshot size:', snapshot.size);
        const pollsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Fetched polls data:', pollsData);
        setPolls(pollsData);
      } catch (err) {
        console.error('Error fetching polls:', err);
        setError('Failed to load polls: ' + err.message);
      } finally {
        setLoading(false);
        console.log('Fetch complete. Loading set to false.');
      }
    };

    fetchPolls();
  }, []); // Empty deps: Fetch polls once on mount

  useEffect(() => {
    const loadPollData = async () => {
      if (dataLoaded) return; // Skip if already loaded
      const timersObj = {};
      const votedObj = {};
      const resultsObj = {};
      for (const poll of polls) {
        timersObj[poll.id] = calculateRemainingTime(poll.endTime);
        votedObj[poll.id] = await getUserVotedLots(poll.id);
        resultsObj[poll.id] = await getResults(poll.id);
      }
      setTimers(timersObj);
      setVotedPolls(votedObj);
      setResults(resultsObj);
      setDataLoaded(true); // Mark as loaded
    };

    if (polls.length > 0 && userLotIds.length > 0) {
      loadPollData();
    }
  }, [polls, userLotIds, getUserVotedLots, getResults, dataLoaded]);

  useEffect(() => {
    // Set up timers
    const interval = setInterval(() => {
      setTimers(prev => {
        const newTimers = { ...prev };
        polls.forEach(poll => {
          newTimers[poll.id] = calculateRemainingTime(poll.endTime);
        });
        return newTimers;
      });
    }, 1000);

    return () => {
      console.log('PollsPage unmounting. Clearing interval.');
      clearInterval(interval);
    };
  }, [polls]); // Depend only on polls for timer updates

  const calculateRemainingTime = (endDate) => {
    const now = moment();
    const end = moment(endDate.toDate()); // Convert Firestore Timestamp to Date
    if (end.isBefore(now)) return 'Closed';
    const duration = moment.duration(end.diff(now));
    if (isNaN(duration.days())) return 'Invalid date'; // Fallback for invalid dates
    return `${duration.days()}d ${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`;
  };

  const handleVote = async (poll) => {
    const pollId = poll.id;
    const selectedOptionIndex = selectedOptions[pollId];
    const selectedLot = userLotIds.length > 1 ? selectedLots[pollId] : userLotIds[0];

    console.log('Attempting vote for poll:', pollId, 'option:', selectedOptionIndex, 'lot:', selectedLot);

    if (selectedOptionIndex === undefined) {
      alert('Please select an option');
      return;
    }
    if (userLotIds.length > 1 && !selectedLot) {
      alert('Please select a lot');
      return;
    }

    setVotingLoading({ ...votingLoading, [pollId]: true });

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/voteOnPollNew', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pollId,
          optionIndex: selectedOptionIndex,
          lotId: selectedLot
        })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Vote failed');
      }
      const data = await response.json();
      console.log('Vote response:', data);

      // Update local state
      setVotedPolls(prev => ({
        ...prev,
        [pollId]: [...(prev[pollId] || []), selectedLot]
      }));

      // Refresh results
      const newResults = await getResults(pollId);
      setResults(prev => ({ ...prev, [pollId]: newResults }));

      alert('Vote submitted successfully');
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to submit vote: ' + error.message);
    } finally {
      setVotingLoading({ ...votingLoading, [pollId]: false});
    }
  };

  if (loading) return <p>Loading polls...</p>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (polls.length === 0) return <p>No polls available at this time.</p>;

  return (
    <div className={`polls-page ${isMobile ? 'polls-page-mobile' : ''}`}>
      <h2 className={`polls-title ${isMobile ? 'polls-title-mobile' : ''}`}>Polls</h2>
      {polls.map(poll => (
        <div key={poll.id} className="poll-card">
          <h3 className="poll-question">{poll.question}</h3>
          {poll.status === 'open' && timers[poll.id] !== 'Closed' && (votedPolls[poll.id] || []).length < (userLotIds || []).length ? (
            <>
              <p className="poll-timer">Time remaining: {timers[poll.id]}</p>
              {poll.options.map((option, index) => (
                <div key={index} className="poll-option">
                  <input
                    type="radio"
                    name={`poll-${poll.id}`}
                    onChange={() => setSelectedOptions({ ...selectedOptions, [poll.id]: index })}
                  />
                  {option.text}
                </div>
              ))}
              {userLotIds.length > 1 && (
                <select onChange={(e) => setSelectedLots({ ...selectedLots, [poll.id]: e.target.value })}>
                  <option value="">Select Lot</option>
                  {userLotIds.filter(lot => ! (votedPolls[poll.id] || []).includes(lot)).map(lot => (
                    <option key={lot} value={lot}>Lot {lot}</option>
                  ))}
                </select>
              )}
              <button className="poll-vote-button" onClick={() => handleVote(poll)} disabled={votingLoading[poll.id]}>
                {votingLoading[poll.id] ? <CircularProgress size={20} /> : 'Vote'}
              </button>
            </>
          ) : (
            <>
              {poll.status === 'open' && <p className="poll-timer">Time remaining: {timers[poll.id]}</p>}
              <div className="poll-results">
                <h4>Results:</h4>
                <PollResults poll={poll} voteCounts={results[poll.id] || {}} isMobile={isMobile} />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default PollsPage;